import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ExternalLink, RefreshCcw, Search } from 'lucide-react';
import { AuthContext } from '../../utils/auth.js';

const TABS = [
    { id: 'email', label: 'Email' },
    { id: 'files', label: 'Files' },
    { id: 'pa', label: 'Pricing analysis' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'checklist', label: 'Checklist' },
    { id: 'report', label: 'Report' },
    { id: 'delivery', label: 'Delivery' },
    { id: 'sharepoint', label: 'SharePoint' },
    { id: 'cost', label: 'OpenAI cost' },
    { id: 'raw', label: 'Raw' },
];

const STATUS_STYLES = {
    completed: 'bg-green-100 text-green-800',
    completed_send_failed: 'bg-amber-100 text-amber-800',
    processing: 'bg-blue-100 text-blue-800',
    received: 'bg-slate-100 text-slate-700',
    failed: 'bg-red-100 text-red-800',
    pass: 'bg-green-100 text-green-800',
    fail: 'bg-red-100 text-red-800',
    needs_review: 'bg-amber-100 text-amber-800',
    warning: 'bg-amber-100 text-amber-800',
    missing: 'bg-orange-100 text-orange-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-slate-100 text-slate-700',
    manual: 'bg-slate-100 text-slate-700',
    dry_run: 'bg-indigo-100 text-indigo-800',
};

function Badge({ value }) {
    const styles = STATUS_STYLES[value] || 'bg-gray-100 text-gray-700';
    return (
        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${styles}`}>
            {value || '—'}
        </span>
    );
}

function formatWhen(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
}

function parseWhen(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
}

function ReceivedCell({ value }) {
    const date = parseWhen(value);
    if (!date) return value || '—';
    return (
        <div className="leading-tight">
            <div>{date.toLocaleDateString()}</div>
            <div className="text-slate-500">
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
        </div>
    );
}

function EmailCell({ value }) {
    if (!value) return '—';
    const email = String(value);
    const at = email.lastIndexOf('@');
    if (at <= 0) {
        return <span className="line-clamp-2 break-all">{email}</span>;
    }
    return (
        <div className="w-[9.5rem] leading-tight">
            <div className="break-all">{email.slice(0, at)}</div>
            <div className="break-all text-slate-500">@{email.slice(at + 1)}</div>
        </div>
    );
}

function money(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function usd(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return n === 0 ? '$0.00' : '—';
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(4)}`;
}

function countLabel(value, singular, plural = `${singular}s`) {
    if (value === null || value === undefined || value === '') return `— ${plural}`;
    return `${value} ${Number(value) === 1 ? singular : plural}`;
}

function runCost(run) {
    const usage = (run && run.usage) || run || {};
    if (Number.isFinite(Number(usage.costUsd)) && Number(usage.costUsd) > 0) {
        return Number(usage.costUsd);
    }
    const input = Number(usage.inputTokens) || 0;
    const output = Number(usage.outputTokens) || 0;
    if (!input && !output) return Number(run && run.costUsd) || 0;
    return (input * 2 + output * 8) / 1e6;
}

function runPages(run) {
    if (Number(run && run.pageCount) > 0) return Number(run.pageCount);
    const files = (run && run.files) || [];
    const fromFiles = files.reduce((sum, file) => sum + (Number(file.pageCount) || 0), 0);
    if (fromFiles) return fromFiles;
    const classified = (run && run.classification) || [];
    return classified.reduce((sum, file) => sum + (Number(file.pageCount) || 0), 0);
}

function runCostPerPage(run) {
    if (Number(run && run.costPerPageUsd) > 0) return Number(run.costPerPageUsd);
    const pages = runPages(run);
    if (!pages) return 0;
    return runCost(run) / pages;
}

function Meta({ label, value }) {
    return (
        <div className="min-w-0">
            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</dt>
            <dd className="mt-1 break-words text-sm text-slate-800">{value || '—'}</dd>
        </div>
    );
}

function Table({ columns, rows }) {
    return (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
            <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                    <tr>
                        {columns.map((column) => (
                            <th key={column} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {column}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {rows.length === 0 ? (
                        <tr>
                            <td className="px-3 py-4 text-sm text-slate-500" colSpan={columns.length}>No rows</td>
                        </tr>
                    ) : rows.map((row, index) => (
                        <tr key={index}>
                            {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-3 py-2 text-sm text-slate-800">{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RunDetail({ run, onArtifact, onResend, resending }) {
    const [tab, setTab] = useState('email');
    const email = run.email || {};
    const rec = run.reconciliation || {};
    const pa = run.pricingAnalysis || {};

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                {TABS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => setTab(item.id)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                            tab === item.id ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200'
                        }`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === 'email' && (
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Meta label="From" value={email.from || email.fromEmail} />
                        <Meta label="Subject" value={email.subject} />
                        <Meta label="Message-ID" value={email.messageId} />
                        <Meta label="Received" value={formatWhen(email.receivedAt)} />
                        <Meta label="Inbound ID" value={run.inboundId} />
                        <Meta label="Source" value={run.source} />
                    </dl>
                    <pre className="mt-4 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-4 text-sm text-slate-800">
                        {email.bodyPlain || 'No text body'}
                    </pre>
                </div>
            )}

            {tab === 'files' && (
                <Table
                    columns={['File', 'Pages', 'Type', 'Confidence', 'Identifiers', 'Zip']}
                    rows={(run.classification || []).map((row) => {
                        const file = ((run.files || []).find((item) => item.fileId === row.fileId || item.filename === row.filename)) || {};
                        return [
                            row.filename,
                            row.pageCount || file.pageCount || '—',
                            <Badge value={row.docType} />,
                            row.confidence,
                            (row.identifiers || []).join(', ') || '—',
                            row.sourceZip || '—',
                        ];
                    })}
                />
            )}

            {tab === 'pa' && (
                <div className="space-y-4">
                    <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                        <dl className="grid grid-cols-2 gap-4">
                            <Meta label="Reference" value={pa.bookingReference} />
                            <Meta label="Booking" value={pa.bookingName} />
                            <Meta label="Agent" value={pa.agentName} />
                            <Meta label="Markup" value={pa.markupPercent != null ? `${pa.markupPercent}%` : ''} />
                            <Meta label="Cost" value={money(pa.totalCost)} />
                            <Meta label="Sell" value={money(pa.totalSell)} />
                        </dl>
                    </div>
                    <Table
                        columns={['Code', 'Supplier', 'Cost', 'Sell', 'Currency']}
                        rows={(pa.summaryBySupplier || []).map((row) => [
                            row.supplierCode,
                            row.supplierName,
                            money(row.totalCost),
                            money(row.totalSell),
                            row.currency,
                        ])}
                    />
                    <Table
                        columns={['Date', 'Supplier', 'Service', 'Inv no', 'Cost', 'Sell']}
                        rows={(pa.lines || []).map((row) => [
                            row.date,
                            `${row.supplierCode} ${row.supplierName}`,
                            row.service,
                            row.supplierInvoiceNo || '',
                            money(row.totalCost),
                            money(row.totalSell),
                        ])}
                    />
                </div>
            )}

            {tab === 'invoices' && (
                <Table
                    columns={['File', 'Issuer', 'IDs', 'Total', 'Verdict', 'Reason']}
                    rows={(run.invoices || []).map((row) => [
                        row.filename,
                        row.issuerName,
                        [row.invoiceNumber, row.bookingId, row.confirmationNo].filter(Boolean).join(' / '),
                        money(row.total),
                        <Badge value={row.validationVerdict} />,
                        row.verdictReason || '',
                    ])}
                />
            )}

            {tab === 'reconciliation' && (
                <div className="space-y-4">
                    <p className="text-sm text-slate-700">{rec.narrative || 'No narrative yet.'}</p>
                    <Table
                        columns={['Code', 'Supplier', 'PA cost', 'Invoices', 'Status']}
                        rows={(rec.suppliers || []).map((row) => [
                            row.supplierCode,
                            row.supplierName,
                            money(row.totalCost),
                            money(row.invoiceTotal),
                            <Badge value={row.missing ? 'missing' : row.amountOk ? 'pass' : 'fail'} />,
                        ])}
                    />
                    <Table
                        columns={['Severity', 'Type', 'Message']}
                        rows={(rec.findings || []).map((row) => [
                            <Badge value={row.severity} />,
                            row.type,
                            row.message,
                        ])}
                    />
                </div>
            )}

            {tab === 'checklist' && (
                <Table
                    columns={['Section', 'Item', 'Status', 'Note']}
                    rows={((run.checklist && run.checklist.rows) || []).map((row) => [
                        row.section,
                        row.item,
                        <Badge value={row.status} />,
                        row.note || '',
                    ])}
                />
            )}

            {tab === 'report' && (
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <button type="button" onClick={() => onArtifact('xlsx')} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white">
                            Download Excel
                        </button>
                        <button type="button" onClick={() => onArtifact('json')} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
                            Download JSON
                        </button>
                    </div>
                    <iframe
                        title="Safari report"
                        sandbox=""
                        srcDoc={(run.report && run.report.html) || '<p>No report</p>'}
                        className="h-[480px] w-full rounded-lg border border-slate-200 bg-white"
                    />
                </div>
            )}

            {tab === 'delivery' && (
                <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-5">
                    <dl className="grid grid-cols-2 gap-4">
                        <Meta label="Sent" value={run.delivery && run.delivery.sent ? 'yes' : 'no'} />
                        <Meta label="To" value={(run.delivery && (run.delivery.to || []).join(', ')) || ''} />
                        <Meta label="Message ID" value={run.delivery && run.delivery.messageId} />
                        <Meta label="Threaded" value={run.delivery && run.delivery.threaded ? 'yes' : 'no'} />
                        <Meta label="Error" value={run.delivery && run.delivery.error} />
                    </dl>
                    <button
                        type="button"
                        disabled={resending}
                        onClick={onResend}
                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                        {resending ? 'Sending…' : 'Resend report'}
                    </button>
                </div>
            )}

            {tab === 'cost' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Total cost</dt>
                            <dd className="mt-1 text-xl font-semibold text-slate-900">{usd(runCost(run))}</dd>
                        </div>
                        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-indigo-600">Cost / page</dt>
                            <dd className="mt-1 text-xl font-semibold text-indigo-950">{runPages(run) ? usd(runCostPerPage(run)) : '—'}</dd>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Pages</dt>
                            <dd className="mt-1 text-xl font-semibold text-slate-900">{runPages(run) || '—'}</dd>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Calls</dt>
                            <dd className="mt-1 text-xl font-semibold text-slate-900">{(run.usage && run.usage.calls) || 0}</dd>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Input tokens</dt>
                            <dd className="mt-1 text-xl font-semibold text-slate-900">{((run.usage && run.usage.inputTokens) || 0).toLocaleString()}</dd>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                            <dt className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Output tokens</dt>
                            <dd className="mt-1 text-xl font-semibold text-slate-900">{((run.usage && run.usage.outputTokens) || 0).toLocaleString()}</dd>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">
                        Cost per page is total OpenAI cost divided by every processed page (PDF pages, one per image, one sheet per Excel).
                        Priced from OpenAI list rates for {(run.usage && run.usage.model) || 'gpt-4.1'}
                        ($2 / $8 per 1M input/output; cached input $0.50).
                    </p>
                    <Table
                        columns={['When', 'Agent', 'File', 'Model', 'In', 'Cached', 'Out', 'Cost']}
                        rows={((run.usage && run.usage.callsLog && run.usage.callsLog.length)
                            ? run.usage.callsLog
                            : (run.usage && (run.usage.inputTokens || run.usage.calls)
                                ? [{
                                    at: run.updatedAt || run.createdAt,
                                    agent: `${run.usage.calls || 1} OpenAI call(s)`,
                                    filename: '',
                                    model: run.usage.model || 'gpt-4.1',
                                    inputTokens: run.usage.inputTokens,
                                    cachedInputTokens: run.usage.cachedInputTokens,
                                    outputTokens: run.usage.outputTokens,
                                    costUsd: runCost(run),
                                }]
                                : [])
                        ).map((row) => [
                            formatWhen(row.at),
                            row.agent,
                            row.filename || '—',
                            row.model,
                            (row.inputTokens || 0).toLocaleString(),
                            (row.cachedInputTokens || 0).toLocaleString(),
                            (row.outputTokens || 0).toLocaleString(),
                            usd(row.costUsd),
                        ])}
                    />
                </div>
            )}

            {tab === 'sharepoint' && (
                <div className="space-y-3">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Live SharePoint writes are disabled. This is a dry-run manifest only.
                    </div>
                    <Meta label="Mode" value={run.sharepoint && run.sharepoint.mode} />
                    <Meta label="Reason" value={run.sharepoint && run.sharepoint.reason} />
                    <Table
                        columns={['Role', 'File', 'Folder', 'Eligible', 'Skip']}
                        rows={(((run.sharepoint && run.sharepoint.manifest) || {}).items || []).map((row) => [
                            row.role,
                            row.filename,
                            row.folder,
                            row.eligible ? 'yes' : 'no',
                            row.skipReason || '',
                        ])}
                    />
                </div>
            )}

            {tab === 'raw' && (
                <pre className="max-h-[480px] overflow-auto rounded-lg bg-slate-50 p-4 text-xs text-slate-700">
                    {JSON.stringify({ usage: run.usage, stages: run.stages, adjudication: run.adjudication, error: run.error }, null, 2)}
                </pre>
            )}
        </div>
    );
}

export default function SafariRuns() {
    const { currentUser } = useContext(AuthContext);
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [resending, setResending] = useState(false);

    const authHeaders = useCallback(async () => ({
        Authorization: `Bearer ${await currentUser.getIdToken()}`,
    }), [currentUser]);

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${serverUrl}/admin/safari/runs`, { headers: await authHeaders() });
            setRuns(response.data.runs || []);
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to load Safari runs');
        } finally {
            setLoading(false);
        }
    }, [authHeaders, serverUrl]);

    useEffect(() => { fetchRuns(); }, [fetchRuns]);

    const openRun = async (run) => {
        setSelected(run);
        try {
            const response = await axios.get(`${serverUrl}/admin/safari/runs/${run.runId}`, { headers: await authHeaders() });
            setDetail(response.data.run);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load run');
        }
    };

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return runs;
        return runs.filter((run) => [
            run.fromEmail,
            run.subject,
            run.bookingReference,
            run.agentName,
            run.status,
            run.verdict,
        ].join(' ').toLowerCase().includes(q));
    }, [runs, searchTerm]);

    const openArtifact = async (kind) => {
        if (!detail) return;
        try {
            const response = await axios.get(
                `${serverUrl}/admin/safari/runs/${detail.runId}/artifact/${kind}`,
                { headers: await authHeaders() }
            );
            if (response.data.url) window.open(response.data.url, '_blank', 'noopener,noreferrer');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Artifact unavailable');
        }
    };

    const resend = async () => {
        if (!detail) return;
        setResending(true);
        try {
            await axios.post(
                `${serverUrl}/admin/safari/runs/${detail.runId}/sendReport`,
                {},
                { headers: await authHeaders() }
            );
            toast.success('Report sent');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Send failed');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="space-y-4 pt-4">
            <div className="flex flex-col gap-3 rounded-xl border border-slate-100 bg-white p-4 shadow-sm lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search booking, sender, subject"
                        className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
                    />
                </div>
                <button type="button" onClick={fetchRuns} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white">
                    <RefreshCcw className="h-4 w-4" />
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Received', 'From', 'Subject', 'Booking', 'Files / pages', 'Matched', 'Missing / issues', 'Status', 'Cost', 'Delivery'].map((h) => (
                                <th key={h} className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {filtered.map((run) => (
                            <tr key={run.runId} className="cursor-pointer hover:bg-slate-50" onClick={() => openRun(run)}>
                                <td className="whitespace-nowrap px-3 py-2 text-sm"><ReceivedCell value={run.createdAt} /></td>
                                <td className="px-3 py-2 text-sm"><EmailCell value={run.fromEmail} /></td>
                                <td className="px-3 py-2 text-sm">
                                    <div className="w-48 truncate 2xl:w-60" title={run.subject || ''}>{run.subject || '—'}</div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">{run.bookingReference}</td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">
                                    <div className="leading-tight">
                                        <div>{countLabel(run.fileCount, 'file')}</div>
                                        <div className="text-slate-500">{countLabel(runPages(run) || null, 'page')}</div>
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">{run.matchedCount}</td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">
                                    <div className="leading-tight">
                                        <div className={Number(run.missingCount) > 0 ? 'text-orange-700' : ''}>{run.missingCount ?? '—'} missing</div>
                                        <div className={Number(run.discrepancyCount) > 0 ? 'text-red-700' : 'text-slate-500'}>{countLabel(run.discrepancyCount, 'issue')}</div>
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm"><Badge value={run.verdict || run.status} /></td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">
                                    <div className="leading-tight">
                                        <div className="font-medium">{usd(runCost(run))}</div>
                                        <div className="text-slate-500">{runPages(run) ? `${usd(runCostPerPage(run))}/pg` : '—'}</div>
                                    </div>
                                </td>
                                <td className="whitespace-nowrap px-3 py-2 text-sm">
                                    <div className="flex flex-col items-start gap-1">
                                        <Badge value={run.sharePointMode || 'dry_run'} />
                                        <span className="text-xs text-slate-500">{run.reportSent ? 'report sent' : 'report not sent'}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Transition show={Boolean(selected)} as={Fragment}>
                <Dialog onClose={() => { setSelected(null); setDetail(null); }} className="relative z-50">
                    <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-slate-900/40" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-start justify-center p-4">
                            <Dialog.Panel className="w-full max-w-6xl rounded-2xl bg-white p-6 shadow-xl">
                                <div className="mb-4 flex items-start justify-between">
                                    <div>
                                        <Dialog.Title className="text-lg font-semibold text-slate-900">
                                            {selected && (selected.bookingReference || selected.subject || selected.runId)}
                                        </Dialog.Title>
                                        <p className="text-sm text-slate-500">
                                            {selected && selected.runId}
                                            {detail ? ` · ${usd(runCost(detail))} OpenAI · ${runPages(detail) || 0} pages · ${runPages(detail) ? usd(runCostPerPage(detail)) : '—'}/page` : ''}
                                        </p>
                                    </div>
                                    <button type="button" onClick={() => { setSelected(null); setDetail(null); }}>
                                        <XMarkIcon className="h-5 w-5 text-slate-500" />
                                    </button>
                                </div>
                                {detail ? (
                                    <RunDetail
                                        run={detail}
                                        onArtifact={openArtifact}
                                        onResend={resend}
                                        resending={resending}
                                    />
                                ) : (
                                    <p className="text-sm text-slate-500">Loading run…</p>
                                )}
                                {detail && detail.report && detail.report.xlsxUrl && (
                                    <a href={detail.report.xlsxUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm text-indigo-600">
                                        Open stored Excel <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                )}
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}
